/**
 * PATH: packages/email-hub/src/feature/hooks/useInboxSuggestions.ts
 * FILE_ID: EMAILHUB-HOOK-INBOX-SUGGESTIONS-001
 * PURPOSE: Lazy fetch of the per-row "Suggested job" chip map for
 *          the inbox view. Talks to the canonical
 *          ``POST /api/emails/suggestions/`` endpoint (Stack 13
 *          MR-A, see ``organisation/views/email_views/
 *          list_detail_views.py`` ``EmailSuggestionsView``) and
 *          returns a `Map<email_id, SuggestedJob>` that InboxPage
 *          falls back to when the row's `email.suggested_job` is
 *          empty.
 *
 *          POST (not GET) — fix for BUG-2026-05-26-EMAIL-SUGGESTIONS-
 *          CORS-PREFLIGHT-414. The legacy GET form encoded the id
 *          list in the URL; at page_size=200 the URL grew to
 *          ~7.5 KB which exceeded nginx's default
 *          ``large_client_header_buffers`` (4 KB) on the prod
 *          backend, so the OPTIONS preflight came back as 400
 *          BEFORE Django could attach CORS headers — the browser
 *          surfaced it as ``"blocked by CORS policy"`` and the
 *          inbox chips silently never appeared. POST with a JSON
 *          body sidesteps the URL-length issue across every
 *          intermediary (nginx, Cloudflare, ALBs, browser
 *          referrers, CDNs).
 *
 * WHY:     BUG-2026-05-24-INBOX-LOAD-SUGGESTIONS-CRIT-PATH —
 *          ``?include_suggestions=true`` on the inbox list endpoint
 *          adds ~22 seconds to every `/api/emails/?folder=inbox`
 *          response on prod (measured 2026-05-24, Newmark org id 6,
 *          page_size=200). Repro:
 *
 *            curl ".../api/emails/?folder=inbox&category__not=spam\
 *                  &page_size=200&include_suggestions=true&tenant=6"
 *            # → 23.3 s
 *            curl ".../api/emails/?folder=inbox&category__not=spam\
 *                  &page_size=200&tenant=6"
 *            # → 0.7 s   (no suggestions = 32× faster)
 *
 *          The architect (Stack 13 MR-A) already built the lazy
 *          endpoint to take suggestions OFF the critical path
 *          ("the FE that wants the chip... fires THIS endpoint with
 *          the visible row IDs to backfill ``suggested_job``
 *          chips"), but the inbox migration never landed —
 *          InboxPage kept ``include_suggestions: true`` on its
 *          ``useEmails`` call. This hook is that migration.
 *
 *          The lazy endpoint itself is still slow (~20 s) because
 *          ``email_job_matcher._load_candidates(organisation_id)``
 *          is uncached — every call rebuilds the full Job + SAM
 *          context map from scratch. A follow-up MR will add a TTL
 *          cache there so chips appear in <1 s. For now this hook
 *          unblocks the critical-path complaint: emails paint in
 *          ~0.7 s and chips backfill in the background without
 *          blocking the operator.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────────┐    ┌───────────────┐
 *   │  inbox rows  │ →  │  visible row IDs │ →  │  GET .../sug/ │
 *   │  (from       │    │  (dedup, cap 200,│    │   ?ids=…      │
 *   │   useEmails) │    │   debounce 150ms)│    │               │
 *   └──────────────┘    └──────────────────┘    └───────┬───────┘
 *                                                       │
 *                                                       ▼
 *                                              ┌──────────────────┐
 *                                              │ Map<id,Suggested>│
 *                                              │ (consumer reads  │
 *                                              │  with fallback   │
 *                                              │  to email.sug…)  │
 *                                              └──────────────────┘
 *
 * Design notes:
 *   1. The hook takes the FULL list of inbox row IDs (typed
 *      `Email[]`) and internally derives the request set:
 *        - cap at 200 (matches `EmailSuggestionsView.MAX_IDS`)
 *        - drop rows that already carry `suggested_job` from the
 *          list response (legacy include_suggestions=true callers)
 *        - drop rows that are already allocated (`job_id` set)
 *      That contains the round-trip cost regardless of how the
 *      caller wires it (cap stays a property of THIS module).
 *   2. Debounced 150 ms so a rapidly-arriving Phase-1 → Phase-2
 *      transition doesn't fire two back-to-back requests against
 *      an endpoint we already know is slow.
 *   3. AbortController per query — a fresh filter change / paste-
 *      new-mailbox-page kills the in-flight slow request rather
 *      than letting it stomp the new filter's result map.
 *   4. SWR semantics: the map is keyed by the comma-joined id list.
 *      Same id-set → cached map. Different id-set → new fetch.
 *      Keeps the chip stable across the Phase-1/Phase-2 enrichment
 *      tick (id set is identical between phases).
 *   5. Empty list → no-op (no fetch, returns empty Map).
 *   6. Auth / tenant — uses the same `emailApi<T>` wrapper as the
 *      list call, so the same Quotech axios pipeline (tenant
 *      header, JWT, 401 retry) applies. No new auth surface.
 *
 * I/O CONTRACT:
 *   Input:  ReadonlyArray<Email | Pick<Email, 'id' | 'job_id' |
 *                                      'suggested_job'>>
 *   Output: Map<string, SuggestedJob>   (only rows with a
 *                                        non-null suggestion)
 *
 * RELATED:
 *   - InboxPage.tsx        : consumer (drops `include_suggestions`
 *                            from useEmails, reads this hook's map)
 *   - useEmails.ts         : owns Email type + SuggestedJob shape
 *   - EmailSuggestionsView : backend lazy endpoint
 *   - email_job_matcher    : _load_candidates (slow, uncached;
 *                            follow-up MR caches it)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { emailApi } from '../lib/api'

import type { Email, SuggestedJob } from './useEmails'

/** Return type for useInboxSuggestions. The map is the active set of
 *  fetched suggestions; ``dismiss(id)`` removes a single row so the
 *  operator's ✕ click on a SuggestedJobChip hides the chip
 *  immediately without waiting for the next refetch (the BE
 *  denylist row prevents the chip from coming back on subsequent
 *  fetches).
 */
export interface InboxSuggestionsResult {
  map: Map<string, SuggestedJob>
  /** Locally drop a suggestion from the map. Idempotent — calling
   *  with an id not present is a no-op. */
  dismiss: (emailId: string) => void
}

/** Hard cap matched to backend ``EmailSuggestionsView.MAX_IDS``. */
export const MAX_SUGGESTION_IDS = 200

/** Debounce in ms before firing the request — see design note 2. */
export const SUGGESTIONS_DEBOUNCE_MS = 150

/** Pure I→O helper. Picks the ids worth requesting from a list of
 *  inbox rows. Exported separately so the contract is unit-testable
 *  without touching React.
 *
 *  Rules:
 *    - row must have a string id
 *    - row must NOT already carry a `suggested_job` from the list
 *      response (don't re-ask for something we already have)
 *    - row must be UNALLOCATED (`job_id` falsy) — allocated rows
 *      render `allocation_summary` / `job_name`, the chip would
 *      duplicate that
 *    - cap at MAX_SUGGESTION_IDS — anything beyond is silently
 *      skipped (matches the BE cap; FE shouldn't pretend it can
 *      ask for more)
 *    - dedup (defensive — should never happen on a real inbox but
 *      a sloppy upstream merge could double-count)
 */
export function pickSuggestionRequestIds(
  rows: ReadonlyArray<
    Pick<Email, 'id' | 'job_id' | 'suggested_job'> | null | undefined
  >,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const row of rows) {
    if (!row) continue
    if (typeof row.id !== 'string' || !row.id) continue
    if (row.suggested_job) continue
    // job_id can be number | string | null | undefined depending on
    // the response phase; coerce to truthy for the gate.
    if (row.job_id) continue
    if (seen.has(row.id)) continue
    seen.add(row.id)
    out.push(row.id)
    if (out.length >= MAX_SUGGESTION_IDS) break
  }
  return out
}

/** Backend response envelope. Keys are the email UUIDs (cast to
 *  string by the BE; see `EmailSuggestionsView.get` final return). */
interface SuggestionsResponse {
  suggestions: Record<string, SuggestedJob>
}

/** React hook. Takes the visible inbox rows and returns a stable
 *  `Map<id, SuggestedJob>` once the lazy fetch resolves. The Map
 *  is empty until the fetch lands; consumers should fall back to
 *  `email.suggested_job` so include_suggestions=true callers still
 *  work in the transition window. */
export function useInboxSuggestions(
  rows: ReadonlyArray<
    Pick<Email, 'id' | 'job_id' | 'suggested_job'> | null | undefined
  >,
): InboxSuggestionsResult {
  // Derive the request id set + a cache key. Memo by joined-string
  // so a parent passing a fresh array literal with identical
  // contents doesn't re-fire the fetch on every render.
  const requestIds = useMemo(() => pickSuggestionRequestIds(rows), [rows])
  const cacheKey = requestIds.join(',')

  const [map, setMap] = useState<Map<string, SuggestedJob>>(() => new Map())
  const inflightRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Cancel any in-flight fetch for a previous id set — see design
    // note 3. Cleanup also runs on unmount which is the same path.
    if (inflightRef.current) {
      inflightRef.current.abort()
      inflightRef.current = null
    }
    if (requestIds.length === 0) {
      // Nothing to ask. Don't blow away the existing map though —
      // a transient "all rows allocated" snapshot shouldn't make
      // chips disappear that we already resolved on a prior tick.
      return
    }
    const controller = new AbortController()
    inflightRef.current = controller
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          // POST + JSON body — see file header for the nginx
          // preflight 414 that motivated the migration off GET.
          const data = await emailApi<SuggestionsResponse>(
            '/api/emails/suggestions/',
            {
              method: 'POST',
              body: JSON.stringify({ ids: requestIds }),
              headers: { 'Content-Type': 'application/json' },
              signal: controller.signal,
            },
          )
          if (controller.signal.aborted) return
          const next = new Map<string, SuggestedJob>()
          const raw = (data && typeof data === 'object'
            ? data.suggestions
            : null) ?? {}
          for (const [id, sug] of Object.entries(raw)) {
            if (id && sug && typeof sug === 'object') {
              next.set(id, sug as SuggestedJob)
            }
          }
          setMap(next)
        } catch (err) {
          // Defensive: a slow / failed suggestions fetch must NEVER
          // surface as a user-visible error toast. The inbox is
          // already painted by this point — failure here just
          // means "no chip", which is the steady-state for plenty
          // of inbox rows anyway. Log to console.debug in dev for
          // when an operator is debugging a missing chip.
          const nodeProcess = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process
          if (nodeProcess !== undefined && nodeProcess.env?.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[useInboxSuggestions] fetch failed', err)
          }
        } finally {
          if (inflightRef.current === controller) {
            inflightRef.current = null
          }
        }
      })()
    }, SUGGESTIONS_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
      if (inflightRef.current === controller) {
        inflightRef.current = null
      }
    }
    // cacheKey collapses requestIds into a primitive — array
    // reference would re-fire every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])

  // Locally drop a suggestion so the chip vanishes immediately when
  // the operator clicks ✕. The BE denylist write (initiated by the
  // chip) keeps the chip from reappearing on the next fetch.
  const dismiss = useCallback((emailId: string) => {
    if (typeof emailId !== 'string' || !emailId) return
    setMap((prev) => {
      if (!prev.has(emailId)) return prev
      const next = new Map(prev)
      next.delete(emailId)
      return next
    })
  }, [])

  return { map, dismiss }
}
